import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { logEvent } from "@/lib/monitoring";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    if (import.meta.env.DEV) {
      try { console.error("[ErrorBoundary]", error, info.componentStack); } catch { /* ignore */ }
    }
    // Strictly non-throwing: swallow sync AND async failures from logging.
    try {
      const maybe = logEvent("ERROR", error?.message || "React render error", {
        stack: error?.stack,
        componentStack: info?.componentStack,
        source: "ErrorBoundary",
      }, "critical") as unknown;
      if (maybe && typeof (maybe as Promise<unknown>).then === "function") {
        (maybe as Promise<unknown>).catch(() => { /* ignore */ });
      }
    } catch { /* ignore */ }
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* ignore */ }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-screen w-full gap-4 p-6 text-center">
          <div aria-hidden className="text-4xl" role="img">⚠️</div>
          <h2 className="text-lg font-semibold text-foreground">
            Something went wrong
          </h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            An unexpected error occurred. Please reload the page to continue.
          </p>
          <Button onClick={this.handleReload} variant="outline">
            Reload page
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
